import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, Input, useToastActions } from '@airclou/ui';
import { createCompany, type CreateCompanyInput } from '@/api/companies';

interface CreateCompanyModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function CreateCompanyModal({ isOpen, onClose, onSuccess }: CreateCompanyModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error } = useToastActions();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState<CreateCompanyInput>({
    name: 'Default Company',
    slug: 'default-company',
    metadata: {},
  });

  const handleSubmit = async () => {
    setLoading(true);

    try {
      await createCompany(formData);
      success(t('companies.companyCreated'), t('common.success'));
      setFormData({ name: '', slug: '', metadata: {} });
      onSuccess();
      onClose();
    } catch (err: any) {
      error(err.message || t('companies.failedToCreate'), t('common.error'));
    } finally {
      setLoading(false);
    }
  };

  const handleNameChange = (value: string) => {
    const slug = value
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .substring(0, 100);
    
    setFormData({ ...formData, name: value, slug });
  };

  const canSubmit = formData.name.trim().length > 0 && formData.slug.trim().length > 0;

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('companies.createCompany')}
      size="md"
      submitText={loading ? t('common.creating') : t('companies.createCompany')}
      canSubmit={canSubmit && !loading}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div>
          <label htmlFor="company-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('companies.companyName')}
          </label>
          <Input
            id="company-name"
            type="text"
            value={formData.name}
            onChange={handleNameChange}
            placeholder="e.g., Engineering"
          />
        </div>

        <div>
          <label htmlFor="company-slug" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.slug')}
          </label>
          <Input
            id="company-slug"
            type="text"
            value={formData.slug}
            onChange={(value) => setFormData({ ...formData, slug: value })}
            placeholder="e.g., engineering"
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('common.slugHint')}
          </p>
        </div>
      </div>
    </FormModal>
  );
}
